/* eslint-disable max-lines */
import { createReducer } from 'typesafe-actions';
import { PDPProductFormatter } from '@westwing/appshell-formatting-helpers';
import { get, camelCaseKeys } from '../../../common/utils/objectUtils';
import findLink from '../utils';
import { defaultErrorHandler, defaultRequestHandler } from '../dataFetching/generators';
import { Dictionary } from '../../../../types/globals';
import generateProductPlaceHolders from './productPlaceholder';
import {
    CrossCategoryNavigation,
    CrossCategoryNavigationCategoryResponse,
    CrossCategoryNavigationResponse,
    DidYouMeanInterface,
    GenericProductsState,
    HydratedFilterValue,
    pathFilterNames,
    ProductResponse,
    ProductsResponse,
    ProductsState,
    ReducedDidYouMeanInterface,
    SearchType,
    SecondaryProductsState,
    LooksInfusionApiResponse,
} from './types';
import {
    BestSellerProductsActions,
    bestSellerProductsActions,
    fetchProductRelativesActions,
    fetchProductsActions,
    fetchSecondaryProductsActions,
    NewProductsRandomizedActions,
    newRandomizedProductsActions,
    ProductRelativesActionsType,
    ProductsActionsType,
    SecondaryProductsActionsType,
    setIsMoodImageHovered,
} from './actions';
import { Filter } from 'Client/components/Generic/FilterAndSortBox/types';

const normalizeCrossCatCategories = (categories: CrossCategoryNavigationCategoryResponse[]): any[] =>
    (categories || []).map((category: CrossCategoryNavigationCategoryResponse) => ({
        name: category.name,
        isSelected: category.isSelected,
        link: findLink(category.links, 'website.product_listing_page'),
    }));

const getCrossNavigation = (crossCategoryNavigation?: CrossCategoryNavigationResponse): CrossCategoryNavigation => {
    // API sometimes returns an empty object
    if (crossCategoryNavigation && Object.keys(crossCategoryNavigation).length > 0) {
        return {
            categories: normalizeCrossCatCategories(crossCategoryNavigation!.categories),
            parentCategory: {
                isRoot: crossCategoryNavigation!.parentCategory.isRoot,
                link: findLink(crossCategoryNavigation!.parentCategory.links, 'website.product_listing_page'),
            },
        };
    }

    return { categories: [], parentCategory: { link: '', isRoot: true } };
};

const mapLooksInfusionsIfAvailable = (looksInfusions?: LooksInfusionApiResponse[]) => {
    if (!looksInfusions) {
        return null;
    }
    return looksInfusions.map(looksInfusion => ({
        relatedSku: looksInfusion.relatedSku || '',
        portraitImg: looksInfusion?.genericImagePortrait?.file?.url || '',
        name: looksInfusion.name || '',
        slug: looksInfusion.slug || '',
        color: looksInfusion.color || '',
        trackingName: looksInfusion.trackingName || '',
    }));
};

const mapDidYouMeanAndRelative = (didYouMean: DidYouMeanInterface): ReducedDidYouMeanInterface => {
    const reduced: ReducedDidYouMeanInterface = { title: '', relLink: '' };
    reduced.title = didYouMean.title;
    reduced.relLink = findLink(didYouMean.links, 'website');
    return reduced;
};

const basicProductsDefaultState = {
    identifier: '',
    products: [],
    alternativeProducts: [],
    isFetching: false,
    didInvalidate: false,
    error: null,
};

export const defaultSecondaryProductsDefaultState = {
    products: {
        allProducts: basicProductsDefaultState,
        newProducts: basicProductsDefaultState,
    },
};

export const defaultProductsState: ProductsState = {
    ...basicProductsDefaultState,
    didYouMean: [],
    filters: [],
    sorting: [],
    totalCount: -1,
    pageTitle: '',
    cmsPageTitle: '',
    crossCategoryNavigation: {
        categories: [],
        parentCategory: { isRoot: true, link: '' },
    },
    links: [],
    pdpUrl: '',
    searchType: SearchType.EMPTY,
    isMoodImageHovered: false,
    looksInfusions: null,
};

export const formatAndSortBrands = (countValues: Dictionary<number>, hydratedValues: Dictionary<HydratedFilterValue>) =>
    Object.keys(countValues)
        .filter(item => hydratedValues[item])
        .map(item => {
            const hydratedValue = hydratedValues[item];
            return {
                count: countValues[item],
                name: hydratedValue.name,
                key: hydratedValue.url_key,
            };
        })
        .sort((a, b) => a.name!.toLowerCase().localeCompare(b.name!.toLowerCase()))
        .reduce(
            (result, item) => {
                if (Object.keys(countValues).length > 1 && item.key === 'westwing-collection') {
                    // duplicate westwing-collection to the first place under a different key
                    // (extra whitespace to aviod displaying a different filter name on StandardFilters)
                    return {
                        valueKeys: { [`${item.name} `]: item.key, ...result.valueKeys, [item.name]: item.key },
                        values: { [`${item.name} `]: item.count, ...result.values, [item.name]: item.count },
                    };
                }
                return {
                    valueKeys: { ...result.valueKeys, [item.name]: item.key },
                    values: { ...result.values, [item.name]: item.count },
                };
            },
            { valueKeys: {}, values: {} }
        );

const getFilters = (payload: ProductsResponse) => {
    if (payload && payload.data && payload.data.filters) {
        return payload.data.filters.map(filter => {
            if (filter.name === 'facet_category') {
                return { ...filter, name: pathFilterNames.categories };
            }
            if (filter.name === 'facet_brand') {
                // eslint-disable-next-line @typescript-eslint/naming-convention, camelcase
                const { values, hydrated_values } = filter as Filter & {
                    // eslint-disable-next-line camelcase
                    hydrated_values: Dictionary<HydratedFilterValue>;
                };

                return { ...filter, name: pathFilterNames.brands, ...formatAndSortBrands(values, hydrated_values) };
            }
            return filter;
        });
    }
    return [];
};

export const primaryProductsReducer = createReducer<ProductsState, ProductsActionsType>(defaultProductsState)
    .handleAction(fetchProductsActions.success, (state, action) => {
        const { data, wwReqId } = action.payload.data;
        const { reducerPayloadOptions } = action.payload;
        // eslint-disable-next-line @typescript-eslint/naming-convention, camelcase
        const { appliedSorting, appliedFilters, search_response_id } = data!;
        const { crossCategoryNavigation, looksInfusions } = data!;
        const formatOptions = { countryCode: '', currency: '', showStrongest: false };
        if (reducerPayloadOptions) {
            formatOptions.countryCode = reducerPayloadOptions.countryCode;
            formatOptions.currency = reducerPayloadOptions.currency;
            formatOptions.showStrongest = !!reducerPayloadOptions.showStrongest;
        }

        const formatProduct = (value: ProductResponse, index: number) =>
            PDPProductFormatter(index, value, appliedSorting, appliedFilters, formatOptions);
        return {
            ...state,
            isFetching: false,
            isFetchingComplete: true,
            didYouMean: get(action.payload.data, 'data.didYouMean', []).map(mapDidYouMeanAndRelative),
            products: get(action.payload.data, 'data.products', []).map(formatProduct),
            alternativeProducts: get(action.payload.data, 'data.alternativeProducts', []).map(formatProduct),
            totalCount: get(action.payload.data, 'data.totalCount', 0),
            filters: getFilters(action.payload.data).map(filter => camelCaseKeys(filter, { deep: false })),
            sorting: get(action.payload.data, 'data.sorting', []),
            didInvalidate: false,
            identifier: action.payload.identifier,
            pageTitle: get(action.payload.data, 'data.meta.title', ''),
            cmsPageTitle: get(action.payload.data, 'data.meta.cms_banner_title', ''),
            links: get(action.payload.data, 'data.links', ''),
            crossCategoryNavigation: getCrossNavigation(crossCategoryNavigation),
            pdpUrl: get(action.payload.data, 'data.meta.pdp_url', ''),
            searchType: get(action.payload.data, 'data.search_type', ''),
            looksInfusions: mapLooksInfusionsIfAvailable(looksInfusions),
            trackingData: {
                wwReqId,
                searchResponseId: search_response_id,
            },
        };
    })
    .handleAction(fetchProductsActions.failure, (state, { payload }) => {
        return {
            ...state,
            isFetching: false,
            isFetchingComplete: true,
            didInvalidate: true,
            products: [],
            alternativeProducts: [],
            error: payload.error,
        };
    })
    .handleAction(fetchProductsActions.request, (state, { payload }) => {
        return {
            ...defaultProductsState,
            isFetching: true,
            isFetchingComplete: undefined,
            totalCount: -1,
            identifier: payload.identifier,
            sorting: state.sorting,
            filters: state.filters,
            products: generateProductPlaceHolders(6),
            alternativeProducts: [],
            pageTitle: '',
            cmsPageTitle: '',
            link: '',
            crossCategoryNavigation: {
                categories: [],
                parentCategory: { isRoot: false, link: '' },
            },
            pdpUrl: '',
            searchType: SearchType.EMPTY,
        };
    })
    .handleAction(setIsMoodImageHovered, (state, { payload: { isHovered } }) => {
        return {
            ...state,
            isMoodImageHovered: isHovered,
        };
    });

export const secondaryProductsReducer = createReducer<SecondaryProductsState, SecondaryProductsActionsType>(
    defaultSecondaryProductsDefaultState
)
    .handleAction(fetchSecondaryProductsActions.request, (state, action) => {
        const { identifier } = action.payload;

        return {
            ...state,
            products: {
                ...state.products,
                [identifier]: {
                    ...state.products[identifier],
                    isFetching: true,
                    products: generateProductPlaceHolders(3),
                },
            },
        };
    })
    .handleAction(fetchSecondaryProductsActions.success, (state, action) => {
        const { identifier } = action.payload;
        const { reducerPayloadOptions } = action.payload;
        const formatOptions = { countryCode: '', currency: '' };
        if (reducerPayloadOptions) {
            formatOptions.countryCode = reducerPayloadOptions.countryCode;
            formatOptions.currency = reducerPayloadOptions.currency;
        }
        return {
            ...state,
            products: {
                ...state.products,
                [identifier]: {
                    ...state.products[identifier],
                    isFetching: false,
                    products: get(
                        action.payload.data,
                        'data.products',
                        []
                    ).map((value: ProductResponse, index: number) =>
                        PDPProductFormatter(index, value, undefined, undefined, formatOptions)
                    ),
                },
            },
        };
    })
    .handleAction(fetchSecondaryProductsActions.failure, (state, action) => {
        const { identifier } = action.payload;

        return {
            ...state,
            products: {
                ...state.products,
                [identifier]: {
                    ...state.products[identifier],
                    isFetching: false,
                    didInvalidate: true,
                    error: action.payload,
                    products: [],
                },
            },
        };
    });

export const newProductRandomizedReducer = createReducer<GenericProductsState, NewProductsRandomizedActions>(
    basicProductsDefaultState
)
    .handleAction(newRandomizedProductsActions.actions.success, (state, action) => {
        const { reducerPayloadOptions } = action.payload;
        const formatOptions = { countryCode: '', currency: '' };
        if (reducerPayloadOptions) {
            formatOptions.countryCode = reducerPayloadOptions.countryCode;
            formatOptions.currency = reducerPayloadOptions.currency;
        }

        return {
            ...state,
            isFetching: false,
            products: get(action.payload.data, 'data.products', []).map((value: ProductResponse, index: number) =>
                PDPProductFormatter(index, value, undefined, undefined, formatOptions)
            ),
        };
    })
    .handleAction(newRandomizedProductsActions.actions.failure, (state, action) => defaultErrorHandler(state, action))
    .handleAction(newRandomizedProductsActions.actions.request, (state, action) =>
        defaultRequestHandler(state, action)
    );

export const bestSellerProductReducer = createReducer<GenericProductsState, BestSellerProductsActions>(
    basicProductsDefaultState
)
    .handleAction(bestSellerProductsActions.actions.success, (state, action) => {
        const { reducerPayloadOptions } = action.payload;
        const formatOptions = { countryCode: '', currency: '' };
        if (reducerPayloadOptions) {
            formatOptions.countryCode = reducerPayloadOptions.countryCode;
            formatOptions.currency = reducerPayloadOptions.currency;
        }

        return {
            ...state,
            isFetching: false,
            products: get(action.payload.data, 'data', []).map((value: ProductResponse, index: number) =>
                PDPProductFormatter(index, value, undefined, undefined, formatOptions)
            ),
        };
    })
    .handleAction(bestSellerProductsActions.actions.failure, (state, action) => defaultErrorHandler(state, action))
    .handleAction(bestSellerProductsActions.actions.request, (state, action) => defaultRequestHandler(state, action));

export const productRelativesReducer = createReducer<GenericProductsState, ProductRelativesActionsType>(
    basicProductsDefaultState
)
    .handleAction(fetchProductRelativesActions.request, (state, action) => {
        return {
            ...state,
            isFetching: true,
            isFetchingComplete: undefined,
            identifier: action.payload.identifier,
            didInvalidate: false,
            products: generateProductPlaceHolders(5),
            error: undefined,
        };
    })
    .handleAction(fetchProductRelativesActions.success, (state, action) => {
        const { reducerPayloadOptions } = action.payload;
        const formatOptions = { countryCode: '', currency: '' };
        if (reducerPayloadOptions) {
            formatOptions.countryCode = reducerPayloadOptions.countryCode;
            formatOptions.currency = reducerPayloadOptions.currency;
        }

        return {
            ...state,
            isFetching: false,
            isFetchingComplete: true,
            didInvalidate: false,
            identifier: action.payload.identifier,
            products: get(action.payload.data, 'data', []).map((value: ProductResponse, index: number) =>
                PDPProductFormatter(index, value, undefined, undefined, formatOptions)
            ),
        };
    })
    .handleAction(fetchProductRelativesActions.failure, (state, action) => {
        return {
            ...state,
            isFetching: false,
            isFetchingComplete: true,
            products: [],
            error: action.payload.error,
        };
    });
